import { useState, useRef, useEffect } from 'react';
import API from '@/lib/api';
import { X, UploadSimple, Sparkle, CheckCircle, ArrowsClockwise, Clipboard, DownloadSimple, Warning, Camera, ClockCounterClockwise, CaretDown, CaretRight, Trash, ArrowCounterClockwise } from '@phosphor-icons/react';

const SAMPLE_IMPORT = `artikelnr;naam;aankoop;basic;standard;premium;bulk;kassa;voorraad
ART001;Bureaustoel Pro;48.00;94.99;89.99;84.99;79.99;92.50;15
ART002;Whiteboard 120x90;28.50;59.95;54.95;49.95;44.95;57.50;5
ART003;Labelprinter;72.50;139.00;129.00;119.00;109.00;135.00;8
ART004;Pennenhouder;4.20;12.95;11.50;9.95;8.50;12.00;50
ART005;Laptop standaard;18.90;42.00;38.00;34.00;29.00;40.00;20`;

const STATUS_META = {
  nieuw: { label: 'Nieuw', color: '#059669', bg: '#ECFDF5', icon: '＋' },
  aangepast: { label: 'Aangepast', color: '#D97706', bg: '#FEF3C7', icon: '✎' },
  controleer: { label: '⚠ Controleer', color: '#DC2626', bg: '#FEE2E2', icon: '!' },
  ongewijzigd: { label: 'Ongewijzigd', color: '#6B7280', bg: '#F9FAFB', icon: '=' },
};

const fmt = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

/**
 * AI Artikel-Import modal:
 * - Stap 1: paste/drop CSV (formaat artikelnr;naam;aankoop;verkoop;voorraad;omschr)
 * - Stap 2: backend analyseert (vergelijkt met bestaande db + AI-rapport)
 * - Stap 3: resultaten-tabel met filter + toepassen
 */
export default function ArtikelImportModal({ open, onClose, onDone, initialCsv }) {
  const [step, setStep] = useState(1);
  const [csvText, setCsvText] = useState('');
  const [analyse, setAnalyse] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [prijsgroepen, setPrijsgroepen] = useState([]);
  // Per-rij per-veld acceptatie: {art_code: {veld_key: true/false}}
  // Default: alle aan (true). Uncheck betekent dat veld NIET meegaat in update.
  const [geaccepteerd, setGeaccepteerd] = useState({});
  // Uitklap-state voor gewijzigde rijen (toont per-veld detail + checkboxes)
  const [expanded, setExpanded] = useState({});
  // Uitgesloten artikel-codes (worden NIET meegestuurd bij import-uitvoeren)
  const [uitgesloten, setUitgesloten] = useState(() => new Set());
  // Backup-historiek panel
  const [showBackups, setShowBackups] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupLabel, setBackupLabel] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    API.get('/artikel-params').then(r => {
      setPrijsgroepen(r.data?.groepen || []);
    }).catch(() => setPrijsgroepen([]));
    // Initial CSV (bv. vanuit prijs-wizard)
    if (initialCsv) setCsvText(initialCsv);
  }, [open, initialCsv]);

  const loadBackups = async () => {
    try {
      const r = await API.get('/artikels/import-backups');
      setBackups(r.data?.backups || []);
    } catch { setBackups([]); }
  };

  if (!open) return null;

  const startAnalyse = async (forceAi = false) => {
    setErr('');
    if (!csvText.trim()) { setErr('Plak eerst CSV-data of sleep een bestand.'); return; }
    setBusy(true);
    setStep(2);
    try {
      const r = await API.post('/artikels/import-analyse', { csv_text: csvText, force_ai_mapping: forceAi });
      setAnalyse(r.data);
      setStep(3);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Analyse mislukt');
      setStep(1);
    } finally { setBusy(false); }
  };

  const uitvoeren = async () => {
    if (!analyse) return;
    // Bouw items met per-veld acceptatie info
    const buildItem = (r) => {
      const item = { ...r };
      // Voor gewijzigde rijen: filter op geaccepteerde_velden
      if (r.status === 'aangepast' || r.status === 'controleer') {
        const perVeld = geaccepteerd[r.art_code];
        if (perVeld) {
          // Verzamel enkel de aangevinkte veldnamen
          const aangevinkt = Object.entries(perVeld).filter(([, v]) => v).map(([k]) => k);
          item.geaccepteerde_velden = aangevinkt;
        }
        // else: geaccepteerd niet aangeraakt → alles doorvoeren (backwards compat)
      }
      return item;
    };
    // Eerst uitgesloten artikels eruit filteren — die wil de gebruiker NIET importeren
    const items = [...analyse.nieuwe, ...analyse.aangepaste, ...analyse.controleer]
      .filter(r => !uitgesloten.has(r.art_code))
      .map(buildItem);
    // Filter uit: rijen met status aangepast/controleer waar geen enkel veld gekozen is
    const filteredItems = items.filter(it => {
      if (it.status === 'nieuw') return true;
      if (Array.isArray(it.geaccepteerde_velden) && it.geaccepteerde_velden.length === 0) return false;
      return true;
    });
    if (filteredItems.length === 0) { alert('Geen artikels om door te voeren (alles is uitgesloten of geen velden aangevinkt).'); return; }
    const uitgeslotenCount = uitgesloten.size;
    const uitgeslotenSuffix = uitgeslotenCount > 0 ? `\n\n🚫 ${uitgeslotenCount} uitgesloten artikel(s) worden NIET geïmporteerd.` : '';
    const msg = `${analyse.nieuwe.length} nieuwe + ${analyse.aangepaste.length} bijgewerkte + ${analyse.controleer.length} ter controle = ${filteredItems.length} artikels importeren?${uitgeslotenSuffix}\n\n💾 Er wordt automatisch een BACKUP gemaakt van alle gewijzigde artikels (1-klik herstel mogelijk).`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await API.post('/artikels/import-uitvoeren', {
        items: filteredItems,
        backup_label: backupLabel || undefined,
      });
      const bmsg = r.data.backup_id ? `\n🛡️ Backup-ID: ${r.data.backup_id} (herstel via "Backups"-knop)` : '';
      alert(`✓ ${r.data.ingevoegd} nieuwe + ${r.data.bijgewerkt} bijgewerkt${r.data.mislukt ? ` · ${r.data.mislukt} mislukt` : ''}.${bmsg}`);
      onDone?.();
      reset();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Import mislukt');
    } finally { setBusy(false); }
  };

  // Toggle per-veld acceptatie voor een rij
  const toggleVeld = (artCode, veldKey, initDiffs) => {
    setGeaccepteerd(prev => {
      const cur = prev[artCode];
      let next;
      if (!cur) {
        // Initialiseer met alle velden aan, behalve de net getoggelde
        next = {};
        (initDiffs || []).forEach(d => { next[d.veld] = d.veld !== veldKey; });
      } else {
        next = { ...cur, [veldKey]: !cur[veldKey] };
      }
      return { ...prev, [artCode]: next };
    });
  };

  // Toggle ALLES voor een rij (select all / deselect all)
  const toggleAlle = (artCode, diffs, waarde) => {
    const next = {};
    diffs.forEach(d => { next[d.veld] = waarde; });
    setGeaccepteerd(prev => ({ ...prev, [artCode]: next }));
  };

  const isVeldGeaccepteerd = (artCode, veldKey) => {
    const row = geaccepteerd[artCode];
    if (!row) return true; // default: alles aan
    return row[veldKey] !== false;
  };

  // Toggle uitsluiting van een artikel (verwijder/herstel in compare-tabel)
  const toggleUitgesloten = (artCode) => {
    setUitgesloten(prev => {
      const next = new Set(prev);
      if (next.has(artCode)) next.delete(artCode);
      else next.add(artCode);
      return next;
    });
  };

  const openBackupsPanel = async () => {
    setShowBackups(true);
    await loadBackups();
  };

  const herstelBackup = async (bid) => {
    if (!window.confirm('Backup herstellen?\n\nAlle artikels in deze backup worden teruggezet naar de snapshot-versie. Latere wijzigingen gaan verloren.')) return;
    setBusy(true);
    try {
      const r = await API.post(`/artikels/import-backups/${bid}/herstel`);
      alert(`✓ ${r.data.hersteld} artikels hersteld`);
      await loadBackups();
      onDone?.();
    } catch (e) {
      alert(e.response?.data?.detail || 'Herstel mislukt');
    } finally { setBusy(false); }
  };

  const reset = () => {
    setStep(1);
    setCsvText('');
    setAnalyse(null);
    setFilter('alle');
    setErr('');
    setUitgesloten(new Set());
    setGeaccepteerd({});
    setExpanded({});
  };

  const handleFile = async (file) => {
    if (!file) return;
    setErr('');
    const fname = (file.name || '').toLowerCase();
    // XLSX / XLS → upload naar backend die converteert naar CSV
    if (fname.endsWith('.xlsx') || fname.endsWith('.xls')) {
      try {
        setBusy(true);
        const fd = new FormData();
        fd.append('file', file);
        const r = await API.post('/artikels/import-xlsx', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data?.csv_text) {
          setCsvText(r.data.csv_text);
        } else {
          setErr('Kon Excel-bestand niet lezen.');
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Excel-upload mislukt');
      } finally { setBusy(false); }
      return;
    }
    // CSV / TXT → lokaal uitlezen
    try {
      const text = await file.text();
      setCsvText(text);
    } catch {
      setErr('Bestand kon niet gelezen worden.');
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const zichtbaar = analyse ? (
    filter === 'alle' ? [...analyse.nieuwe, ...analyse.aangepaste, ...analyse.controleer, ...analyse.ongewijzigd]
      : filter === 'nieuw' ? analyse.nieuwe
      : filter === 'aangepast' ? analyse.aangepaste
      : filter === 'controleer' ? analyse.controleer
      : filter === 'uitgesloten' ? [...analyse.nieuwe, ...analyse.aangepaste, ...analyse.controleer, ...analyse.ongewijzigd].filter(r => uitgesloten.has(r.art_code))
      : analyse.ongewijzigd
  ) : [];

  // Hoeveel items worden effectief geïmporteerd (na uitsluiting)
  const teImporteren = analyse
    ? [...analyse.nieuwe, ...analyse.aangepaste, ...analyse.controleer].filter(r => !uitgesloten.has(r.art_code)).length
    : 0;

  const copyRapport = () => {
    if (analyse?.ai_rapport) {
      navigator.clipboard?.writeText(analyse.ai_rapport).then(() => alert('Rapport gekopieerd'));
    }
  };

  const exportCsv = () => {
    if (!analyse) return;
    const rows = [...analyse.nieuwe, ...analyse.aangepaste, ...analyse.controleer, ...analyse.ongewijzigd];
    const prijsKeys = prijsgroepen.map(g => g.key);
    const header = ['Art.code', 'Naam', 'Aankoop', ...prijsKeys, 'Voorraad', 'Status'].join(';');
    const csv = [header]
      .concat(rows.map(r => [
        r.art_code,
        r.omschrijving,
        r.aankoopprijs,
        ...prijsKeys.map(k => (r.prijzen || {})[k] ?? ''),
        r.voorraad,
        r.status,
      ].join(';')))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-analyse-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Blanco-template: header + 2 voorbeeldregels met ALLE artikel-velden
  // Zo kan gebruiker een artikel met EAN, BTW, gewicht, HS-code, etc. direct importeren.
  const downloadBlancoTemplate = () => {
    const prijsKeys = prijsgroepen.length > 0 ? prijsgroepen.map(g => g.key) : ['basic', 'standard', 'premium', 'bulk'];
    const hasKassa = prijsKeys.includes('kassa');
    const prijsCols = hasKassa ? prijsKeys : [...prijsKeys, 'kassa'];
    // Volledige kolom-volgorde — zelfde als artikel-formulier
    const headerCols = [
      'artikelnr', 'ean', 'omschrijving',
      'categorie', 'familie', 'leverancier',
      'eenheid', 'pack_grootte', 'btw',
      'aankoop', ...prijsCols,
      'voorraad', 'min_voorraad',
      'gewicht_kg', 'volume_m3',
      'hs_code', 'oorsprong_land',
      'toon_ean_op_document',
    ];
    const prijsVoorKey = (k, set) => {
      if (k === 'basic') return set === 1 ? '50.00' : '24.95';
      if (k === 'standard') return set === 1 ? '47.00' : '22.95';
      if (k === 'premium') return set === 1 ? '45.00' : '21.00';
      if (k === 'bulk') return set === 1 ? '42.00' : '19.50';
      if (k === 'kassa') return set === 1 ? '48.50' : '24.00';
      return set === 1 ? '50.00' : '24.95';
    };
    const voorbeeldRij = [
      'ART001',                         // artikelnr
      '5412345678901',                  // ean (EAN-13)
      'Voorbeeldartikel A',             // omschrijving
      'Kantoor',                        // categorie
      '',                               // familie (optioneel)
      'Leverancier BV',                 // leverancier
      'stuk',                           // eenheid (stuk/pack/meter/kg/liter/uur)
      '',                               // pack_grootte (enkel bij eenheid=pack)
      '21',                             // btw (21/12/6/0)
      '25.00',                          // aankoop
      ...prijsCols.map(k => prijsVoorKey(k, 1)),
      '100',                            // voorraad
      '10',                             // min_voorraad
      '2.500',                          // gewicht_kg
      '0.0120',                         // volume_m3
      '8471.30.00',                     // hs_code
      'BE',                             // oorsprong_land (ISO 2-letter)
      'nee',                            // toon_ean_op_document (ja/nee)
    ];
    const voorbeeldRij2 = [
      'ART002',
      '',                               // geen EAN
      'Voorbeeldartikel B (pack van 12)',
      'Voeding',
      'Pack-familie',
      'Andere Leverancier',
      'pack',
      '12',                             // pack-grootte
      '6',                              // 6% BTW
      '12.50',
      ...prijsCols.map(k => prijsVoorKey(k, 2)),
      '50',
      '5',
      '1.200',
      '',
      '',
      'FR',
      'ja',                             // EAN wél op document
    ];
    const csv = [
      headerCols.join(';'),
      voorbeeldRij.join(';'),
      voorbeeldRij2.join(';'),
    ].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM voor Excel
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `artikels-import-template-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => !busy && onClose()} data-testid="artikel-import-modal">
      <div className="w-full max-w-5xl bg-white shadow-2xl flex flex-col" style={{ maxHeight: '95vh' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 bg-[#002FA7] text-white flex items-center justify-between">
          <h3 className="font-bold text-base inline-flex items-center gap-2">
            <Sparkle size={18} weight="fill" /> AI Artikel-import
            <span className="text-[10px] bg-white/20 px-2 py-0.5 uppercase tracking-wider">Stap {step}/3</span>
          </h3>
          <div className="flex gap-1 items-center">
            <button onClick={openBackupsPanel} className="text-[11px] inline-flex items-center gap-1 px-2 py-1 hover:bg-white/10 rounded" data-testid="import-open-backups" title="Import-backups bekijken en herstellen">
              <ClockCounterClockwise size={14} weight="duotone" /> Backups
            </button>
            <button onClick={onClose} disabled={busy} className="p-1.5 hover:bg-white/10" data-testid="import-modal-close"><X size={18} weight="bold" /></button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* STAP 1 — Invoer */}
          {step === 1 && (
            <div className="p-5 space-y-4">
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-1">Sleep bestand of plak CSV-data</h4>
                <p className="text-[11px] text-gray-500 mb-1">
                  <strong>Gebruik een header-rij</strong> met kolomnamen voor flexibele mapping. Ondersteunde kolommen:
                </p>
                <div className="flex flex-wrap gap-1 mb-1 text-[10px]">
                  <code className="bg-gray-100 px-1.5 py-0.5">artikelnr</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">ean</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">omschrijving</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">categorie</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">familie</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">leverancier</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">eenheid</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">pack_grootte</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">btw</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">aankoop</code>
                  {prijsgroepen.length > 0 ? prijsgroepen.map(g => (
                    <code key={g.key} className="px-1.5 py-0.5 font-bold" style={{ background: `${g.color}20`, color: g.color }}>{g.key}</code>
                  )) : ['basic', 'standard', 'premium', 'bulk'].map(k => (
                    <code key={k} className="bg-blue-50 px-1.5 py-0.5 text-blue-700">{k}</code>
                  ))}
                  {!prijsgroepen.some(g => g.key === 'kassa') && (
                    <code className="bg-red-50 px-1.5 py-0.5 text-red-700">kassa</code>
                  )}
                  <code className="bg-gray-100 px-1.5 py-0.5">voorraad</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">min_voorraad</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">gewicht_kg</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">volume_m3</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">hs_code</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">oorsprong_land</code>
                  <code className="bg-gray-100 px-1.5 py-0.5">toon_ean_op_document</code>
                </div>
                <p className="text-[11px] text-gray-500">
                  Delimiter: semicolons, komma's of tabs. Zonder header werkt oude volgorde: <code className="bg-gray-100 px-1">artikelnr;naam;aankoop;verkoop;voorraad;omschrijving</code>
                </p>
              </div>

              <div
                className="border-2 border-dashed border-gray-300 p-8 text-center hover:border-[#002FA7] bg-gray-50 cursor-pointer transition-colors"
                onDragOver={e => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
                data-testid="import-drop-zone"
              >
                <UploadSimple size={36} className="mx-auto text-gray-400 mb-2" weight="duotone" />
                <div className="text-sm font-bold text-gray-700">Sleep uw .xlsx / .csv / .txt bestand hierheen</div>
                <div className="text-[11px] text-gray-500 mt-1">✨ Excel-bestanden worden automatisch omgezet · of klik om te bladeren</div>
                <input ref={fileRef} type="file" accept=".csv,.txt,.tsv,.xlsx,.xls" className="hidden" onChange={e => handleFile(e.target.files?.[0])} data-testid="import-file-input" />
              </div>

              {/* Template-download */}
              <div className="flex items-center gap-2 justify-center text-xs pt-1">
                <span className="text-gray-500">Nieuw hier?</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const resp = await API.get('/artikels/import-template?format=xlsx', { responseType: 'blob' });
                      const url = URL.createObjectURL(resp.data);
                      const a = document.createElement('a');
                      a.href = url; a.download = 'artikel-import-template.xlsx'; a.click();
                      URL.revokeObjectURL(url);
                    } catch { setErr('Template-download mislukt'); }
                  }}
                  className="inline-flex items-center gap-1 text-[#002FA7] hover:underline font-bold"
                  data-testid="import-download-template"
                >
                  <DownloadSimple size={12} weight="bold" /> Download voorbeeld-Excel
                </button>
                <span className="text-gray-400">·</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const resp = await API.get('/artikels/import-template?format=csv', { responseType: 'blob' });
                      const url = URL.createObjectURL(resp.data);
                      const a = document.createElement('a');
                      a.href = url; a.download = 'artikel-import-template.csv'; a.click();
                      URL.revokeObjectURL(url);
                    } catch { setErr('Template-download mislukt'); }
                  }}
                  className="text-gray-600 hover:text-[#002FA7] hover:underline"
                  data-testid="import-download-template-csv"
                >
                  of CSV
                </button>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600 block mb-1">Of plak hier</label>
                <textarea
                  className="field-input font-mono text-xs w-full"
                  rows={8}
                  value={csvText}
                  onChange={e => setCsvText(e.target.value)}
                  placeholder={SAMPLE_IMPORT}
                  data-testid="import-csv-textarea"
                />
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <button onClick={() => setCsvText(SAMPLE_IMPORT)} className="btn btn-ghost btn-sm text-[11px]" data-testid="import-sample">📋 Voorbeeld laden</button>
                  <button
                    onClick={downloadBlancoTemplate}
                    className="btn btn-secondary btn-sm text-[11px] inline-flex items-center gap-1.5 font-bold"
                    title="Download een leeg CSV-bestand met de juiste kolommen om in Excel in te vullen"
                    data-testid="import-blanco-template"
                  >
                    <DownloadSimple size={12} weight="bold" /> Blanco template (.csv)
                  </button>
                  <span className="text-[10px] text-gray-500 italic">— Open in Excel, vul uw artikels in, sla op als CSV en sleep hierheen</span>
                </div>
              </div>

              {err && <div className="px-3 py-2 bg-red-50 border border-red-200 text-xs text-red-800 font-bold" data-testid="import-err">{err}</div>}

              <div className="flex gap-2 flex-wrap">
                <button onClick={() => startAnalyse(false)} disabled={busy || !csvText.trim()} className="btn btn-primary font-bold inline-flex items-center gap-2" data-testid="import-start-analyse">
                  <Sparkle size={14} weight="fill" /> AI analyse starten
                </button>
                <button onClick={() => startAnalyse(true)} disabled={busy || !csvText.trim()} className="btn btn-secondary inline-flex items-center gap-2 text-[11px]" title="Forceer AI om kolommen te mappen (handig bij ongewone headers)" data-testid="import-force-ai-mapping">
                  🤖 Forceer AI-kolom-mapping
                </button>
                <button onClick={() => setCsvText('')} className="btn btn-secondary">Wissen</button>
              </div>
            </div>
          )}

          {/* STAP 2 — Laden */}
          {step === 2 && (
            <div className="p-10 text-center" data-testid="import-laden">
              <ArrowsClockwise size={38} className="mx-auto text-[#002FA7] animate-spin mb-3" weight="bold" />
              <div className="text-sm font-bold text-gray-900">Artikelen analyseren...</div>
              <div className="text-[11px] text-gray-500 mt-1">AI vergelijkt met uw bestaande database (kan 10-20s duren).</div>
            </div>
          )}

          {/* STAP 3 — Resultaat */}
          {step === 3 && analyse && (
            <div className="p-5 space-y-4">
              {analyse.ai_mapping_used && analyse.mapping_info && (
                <div className="border-2 border-[#7C3AED]/30 bg-[#F5F3FF] p-3 text-xs text-[#4C1D95]" data-testid="ai-mapping-banner">
                  <div className="font-bold inline-flex items-center gap-1 mb-1">
                    <Sparkle size={12} weight="fill" /> AI heeft uw CSV-kolommen automatisch gemapt
                  </div>
                  <div className="italic">{analyse.mapping_info.redenering || 'Kolommen geïdentificeerd op basis van inhoud en header-aliassen.'}</div>
                  <div className="mt-1 font-mono text-[10px]">Mapping: {Object.entries(analyse.mapping_info.mapping || {}).map(([i, v]) => `kolom ${i}→${v}`).join(' · ')}</div>
                </div>
              )}

              {/* CSV-duplicaten waarschuwing */}
              {analyse.csv_duplicates && analyse.csv_duplicates.length > 0 && (
                <div className="border-2 border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="import-dup-banner">
                  <div className="font-bold inline-flex items-center gap-1 mb-1">
                    <Warning size={12} weight="fill" /> {analyse.csv_duplicates.length} duplicate(n) in CSV gedetecteerd
                  </div>
                  <div>De laatste rij per artikelcode werd behouden. Controleer: {analyse.csv_duplicates.slice(0, 8).map(d => d.art_code).join(', ')}{analyse.csv_duplicates.length > 8 ? '…' : ''}</div>
                </div>
              )}

              {/* Validatie-fouten */}
              {analyse.validatie_fouten && analyse.validatie_fouten.length > 0 && (
                <div className="border-2 border-red-300 bg-red-50 p-3 text-xs text-red-900" data-testid="import-val-banner">
                  <div className="font-bold inline-flex items-center gap-1 mb-1">
                    <Warning size={12} weight="fill" /> {analyse.validatie_fouten.length} validatie-waarschuwing(en)
                  </div>
                  <ul className="list-disc ml-5 space-y-0.5">
                    {analyse.validatie_fouten.slice(0, 10).map((v, i) => (
                      <li key={i}>
                        <code className="font-mono font-bold">{v.art_code}</code>: {v.veld} = {v.waarde}
                        {v.fout === 'negatief' && ' (negatieve waarde niet toegestaan)'}
                        {v.fout === 'onder_aankoop' && ` (verkoopprijs onder aankoop €${v.aankoop})`}
                      </li>
                    ))}
                    {analyse.validatie_fouten.length > 10 && <li className="italic">+ {analyse.validatie_fouten.length - 10} meer…</li>}
                  </ul>
                </div>
              )}

              {/* KPI tiles */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <KPI label="Totaal" value={analyse.totaal} color="#002FA7" />
                <KPI label="Nieuw" value={analyse.nieuwe.length} color="#059669" onClick={() => setFilter('nieuw')} active={filter === 'nieuw'} />
                <KPI label="Aangepast" value={analyse.aangepaste.length} color="#D97706" onClick={() => setFilter('aangepast')} active={filter === 'aangepast'} />
                <KPI label="Controleer" value={analyse.controleer.length} color="#DC2626" onClick={() => setFilter('controleer')} active={filter === 'controleer'} />
                <KPI label="Ongewijzigd" value={analyse.ongewijzigd.length} color="#6B7280" onClick={() => setFilter('ongewijzigd')} active={filter === 'ongewijzigd'} />
              </div>

              {/* AI rapport */}
              {analyse.ai_rapport && (
                <div className="border-2 border-[#002FA7]/20 bg-[#F0F4FF]/40 p-4" data-testid="import-ai-rapport">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-[#002FA7] inline-flex items-center gap-1">
                      <Sparkle size={14} weight="fill" /> AI-Rapport &amp; aanbevelingen
                    </h4>
                    <button onClick={copyRapport} className="btn btn-ghost btn-sm text-[11px] inline-flex items-center gap-1"><Clipboard size={11} /> Kopieer</button>
                  </div>
                  <div className="text-xs text-gray-800 whitespace-pre-wrap leading-relaxed">{analyse.ai_rapport}</div>
                </div>
              )}

              {/* Filter tabs */}
              <div className="flex items-center gap-1 border-b border-gray-200 flex-wrap">
                {['alle', 'nieuw', 'aangepast', 'controleer', 'ongewijzigd'].map(f => (
                  <button
                    key={f}
                    className={`px-3 py-2 text-xs font-bold capitalize ${filter === f ? 'text-[#002FA7] border-b-2 border-[#002FA7]' : 'text-gray-500'}`}
                    onClick={() => setFilter(f)}
                    data-testid={`import-filter-${f}`}
                  >
                    {f === 'alle' ? `Alle (${analyse.totaal})` : `${STATUS_META[f]?.label || f} (${analyse[f === 'alle' ? 'totaal' : f === 'nieuw' ? 'nieuwe' : f === 'aangepast' ? 'aangepaste' : f]?.length || 0})`}
                  </button>
                ))}
                {uitgesloten.size > 0 && (
                  <button
                    className={`px-3 py-2 text-xs font-bold ${filter === 'uitgesloten' ? 'text-red-700 border-b-2 border-red-600' : 'text-red-500'}`}
                    onClick={() => setFilter('uitgesloten')}
                    data-testid="import-filter-uitgesloten"
                    title="Toon uitgesloten artikels"
                  >
                    🚫 Uitgesloten ({uitgesloten.size})
                  </button>
                )}
                {/* Bulk-actie: uitsluit alle in huidige filter (behalve "ongewijzigd" en "uitgesloten") */}
                {filter !== 'ongewijzigd' && filter !== 'uitgesloten' && zichtbaar.length > 0 && (() => {
                  const teUitsluiten = zichtbaar.filter(r => r.status !== 'ongewijzigd' && !uitgesloten.has(r.art_code));
                  if (teUitsluiten.length === 0) return null;
                  return (
                    <button
                      onClick={() => {
                        if (!window.confirm(`${teUitsluiten.length} artikel(s) uitsluiten van import?`)) return;
                        setUitgesloten(prev => {
                          const next = new Set(prev);
                          teUitsluiten.forEach(r => next.add(r.art_code));
                          return next;
                        });
                      }}
                      className="text-[11px] text-red-600 hover:text-red-700 hover:underline ml-1 inline-flex items-center gap-1"
                      title={`Sluit alle ${teUitsluiten.length} zichtbare artikels uit`}
                      data-testid="import-bulk-uitsluit"
                    >
                      <Trash size={11} weight="bold" /> Sluit alle uit ({teUitsluiten.length})
                    </button>
                  );
                })()}
                <button onClick={exportCsv} className="ml-auto btn btn-ghost btn-sm text-[11px] inline-flex items-center gap-1" data-testid="import-export-csv">
                  <DownloadSimple size={11} /> Export CSV
                </button>
              </div>

              {/* Tabel */}
              <div className="border border-gray-200 overflow-auto max-h-[26rem]">
                <table className="w-full text-xs" style={{ minWidth: 760 + prijsgroepen.length * 70 }}>
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-[10px] uppercase tracking-wider text-gray-600 font-bold">
                      <th className="text-center px-2 py-1.5 w-6"></th>
                      <th className="text-center px-2 py-1.5 w-14">Foto</th>
                      <th className="text-left px-2 py-1.5">Art.code</th>
                      <th className="text-left px-2 py-1.5">Naam</th>
                      <th className="text-right px-2 py-1.5">Aankoop</th>
                      {prijsgroepen.map(g => (
                        <th key={g.key} className="text-right px-2 py-1.5" style={{ color: g.color }}>{g.label}</th>
                      ))}
                      <th className="text-right px-2 py-1.5">Voorraad</th>
                      <th className="text-left px-2 py-1.5">Status / Verschillen</th>
                      <th
                        className="text-center px-2 py-1.5"
                        style={{ position: 'sticky', right: 0, background: '#F9FAFB', zIndex: 12, borderLeft: '2px solid #E5E7EB', minWidth: 110, boxShadow: '-4px 0 6px -2px rgba(0,0,0,0.08)' }}
                      >Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zichtbaar.length === 0 ? (
                      <tr><td colSpan={8 + prijsgroepen.length} className="text-center py-6 text-gray-400 italic">
                        {filter === 'uitgesloten' ? 'Geen uitgesloten artikels.' : 'Geen items in deze categorie.'}
                      </td></tr>
                    ) : zichtbaar.map((r, i) => {
                      const meta = STATUS_META[r.status] || STATUS_META.ongewijzigd;
                      const heeftDiffs = (r.veld_diffs || []).length > 0;
                      const isOpen = expanded[r.art_code];
                      const isUitgesloten = uitgesloten.has(r.art_code);
                      const isOngewijzigd = r.status === 'ongewijzigd';
                      const fotoUrl = r.huidig?.foto_url;
                      const fotoSrc = fotoUrl
                        ? (fotoUrl.startsWith('http') ? fotoUrl : `${process.env.REACT_APP_BACKEND_URL}${fotoUrl}`)
                        : null;
                      const acceptedCount = (r.veld_diffs || []).filter(d => isVeldGeaccepteerd(r.art_code, d.veld)).length;
                      const rowKey = `${r.art_code}-${i}`;
                      // Uitgesloten: line-through + grijze tekst op niet-sticky cellen.
                      // GEEN CSS opacity op de rij — dat zou ook de Herstel-knop wazig maken.
                      const cellStyle = isUitgesloten ? { textDecoration: 'line-through', color: '#9CA3AF' } : {};
                      return [
                        <tr key={`row-${rowKey}`} className={`border-t border-gray-100 hover:bg-gray-50 ${isUitgesloten ? 'bg-red-50/40' : ''}`} data-testid={`import-rij-${r.art_code}`}>
                            <td className="px-2 py-1.5 text-center" style={cellStyle}>
                              {heeftDiffs && !isUitgesloten ? (
                                <button onClick={() => setExpanded(p => ({ ...p, [r.art_code]: !p[r.art_code] }))} className="text-gray-400 hover:text-[#002FA7]" data-testid={`import-toggle-${r.art_code}`}>
                                  {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                                </button>
                              ) : null}
                            </td>
                            <td className="px-2 py-1.5 text-center" style={cellStyle}>
                              {fotoSrc ? (
                                <img src={fotoSrc} alt="" className="w-10 h-10 object-cover rounded border border-gray-200" title="Bestaande foto" />
                              ) : r.status === 'nieuw' ? (
                                <div className="w-10 h-10 bg-emerald-50 border border-emerald-200 rounded flex items-center justify-center" title="Nieuw artikel">
                                  <span className="text-emerald-600 text-lg font-bold">+</span>
                                </div>
                              ) : (
                                <div className="w-10 h-10 bg-gray-100 border border-gray-200 rounded flex items-center justify-center" title="Geen foto">
                                  <Camera size={14} className="text-gray-400" />
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-1.5 font-mono font-bold" style={cellStyle}>{r.art_code}</td>
                            <td className="px-2 py-1.5 truncate max-w-[200px]" title={r.omschrijving} style={cellStyle}>{r.omschrijving}</td>
                            <td className="px-2 py-1.5 text-right font-mono" style={cellStyle}>{fmt(r.aankoopprijs)}</td>
                            {prijsgroepen.map(g => {
                              const val = (r.prijzen || {})[g.key];
                              const huidig = r.huidig?.prijzen?.[g.key];
                              const gewijzigd = huidig !== undefined && val && Math.abs(val - huidig) > 0.009;
                              const baseStyle = gewijzigd && !isUitgesloten ? { background: `${g.color}15`, fontWeight: 700, color: g.color } : {};
                              return (
                                <td key={g.key} className="px-2 py-1.5 text-right font-mono" style={{ ...baseStyle, ...cellStyle }}>
                                  {val ? fmt(val) : <span className="text-gray-300">-</span>}
                                </td>
                              );
                            })}
                            <td className="px-2 py-1.5 text-right font-mono" style={cellStyle}>{r.voorraad}</td>
                            <td className="px-2 py-1.5">
                              {isUitgesloten ? (
                                <span className="inline-block px-1.5 py-0.5 font-bold text-[10px] bg-red-100 text-red-700">🚫 UITGESLOTEN</span>
                              ) : (
                                <>
                                  <span className="inline-block px-1.5 py-0.5 font-bold text-[10px] mr-1" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                                  {heeftDiffs && (
                                    <span className="text-[10px] text-gray-600">
                                      {acceptedCount}/{r.veld_diffs.length} velden ✓
                                    </span>
                                  )}
                                </>
                              )}
                            </td>
                            <td
                              className="px-2 py-1.5 text-center"
                              style={{
                                textDecoration: 'none',
                                position: 'sticky',
                                right: 0,
                                background: isUitgesloten ? '#FEF2F2' : 'white',
                                borderLeft: '2px solid #E5E7EB',
                                zIndex: 5,
                                boxShadow: '-4px 0 6px -2px rgba(0,0,0,0.08)',
                              }}
                            >
                              {isOngewijzigd ? (
                                <span className="text-[10px] text-gray-300" title="Ongewijzigde artikels worden sowieso niet aangepast">—</span>
                              ) : isUitgesloten ? (
                                <button
                                  onClick={() => toggleUitgesloten(r.art_code)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50 border border-emerald-200 rounded"
                                  title="Artikel terug opnemen in de import"
                                  data-testid={`import-herstel-${r.art_code}`}
                                >
                                  <ArrowCounterClockwise size={11} weight="bold" /> Herstel
                                </button>
                              ) : (
                                <button
                                  onClick={() => toggleUitgesloten(r.art_code)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-red-600 hover:bg-red-50 border border-red-200 rounded"
                                  title="Dit artikel niet importeren"
                                  data-testid={`import-uitsluit-${r.art_code}`}
                                >
                                  <Trash size={11} weight="bold" /> Uitsluit
                                </button>
                              )}
                            </td>
                          </tr>,
                          /* Expandable row: per-veld checkboxes */
                          isOpen && heeftDiffs && !isUitgesloten ? (
                            <tr key={`exp-${rowKey}`} className="border-t border-gray-100 bg-blue-50/30">
                              <td colSpan={8 + prijsgroepen.length} className="px-4 py-3">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-[11px] font-bold text-gray-700">📝 Wijzigingen per veld — vink aan wat je wil doorvoeren</div>
                                  <div className="flex gap-2">
                                    <button className="text-[10px] text-[#002FA7] hover:underline" onClick={() => toggleAlle(r.art_code, r.veld_diffs, true)} data-testid={`import-sel-all-${r.art_code}`}>Alles aan</button>
                                    <span className="text-gray-300">|</span>
                                    <button className="text-[10px] text-red-600 hover:underline" onClick={() => toggleAlle(r.art_code, r.veld_diffs, false)} data-testid={`import-sel-none-${r.art_code}`}>Alles uit</button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                                  {(r.veld_diffs || []).map((d, idx) => {
                                    const checked = isVeldGeaccepteerd(r.art_code, d.veld);
                                    const isGroot = d.is_groot;
                                    return (
                                      <label
                                        key={idx}
                                        className={`flex items-center gap-2 px-2 py-1.5 border rounded cursor-pointer text-[11px] ${
                                          checked
                                            ? isGroot ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'
                                            : 'border-gray-200 bg-gray-50 opacity-60'
                                        }`}
                                        data-testid={`import-veld-${r.art_code}-${d.veld}`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => toggleVeld(r.art_code, d.veld, r.veld_diffs)}
                                          className="w-3.5 h-3.5"
                                        />
                                        <div className="flex-1">
                                          <div className="font-bold text-gray-700">
                                            {d.label} {isGroot && <span className="text-amber-600">⚠</span>}
                                          </div>
                                          <div className="text-gray-600 font-mono">
                                            <span className="line-through text-red-500">{d.oud || '—'}</span>
                                            <span className="mx-1 text-gray-400">→</span>
                                            <span className="text-emerald-700 font-bold">{d.nieuw}</span>
                                            {d.pct != null && <span className="ml-1 text-gray-500">({d.pct >= 0 ? '+' : ''}{d.pct.toFixed(1)}%)</span>}
                                          </div>
                                        </div>
                                      </label>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          ) : null
                        ];
                      })}
                  </tbody>
                </table>
              </div>

              {/* Backup-label input */}
              <div className="flex items-center gap-2">
                <label className="text-[11px] font-bold text-gray-600 whitespace-nowrap">🛡️ Backup-label:</label>
                <input
                  type="text"
                  value={backupLabel}
                  onChange={e => setBackupLabel(e.target.value)}
                  placeholder="Bijv. 'Prijs-update Q1 2026'"
                  className="field-input text-xs flex-1"
                  data-testid="import-backup-label"
                />
                <span className="text-[10px] text-gray-500 italic">— optioneel, helpt bij herkennen in herstel-lijst</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 3 && analyse && (
          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
            <div className="flex gap-2 items-center">
              <button onClick={reset} className="btn btn-secondary inline-flex items-center gap-1.5" data-testid="import-opnieuw">
                <ArrowsClockwise size={13} /> Nieuwe import
              </button>
              {uitgesloten.size > 0 && (
                <button
                  onClick={() => setUitgesloten(new Set())}
                  className="text-[11px] text-red-700 hover:underline font-bold inline-flex items-center gap-1"
                  title="Herstel alle uitgesloten artikels"
                  data-testid="import-uitsluit-clear"
                >
                  <ArrowCounterClockwise size={11} weight="bold" /> Herstel alle uitgesloten ({uitgesloten.size})
                </button>
              )}
            </div>
            <div className="flex gap-2 items-center">
              {uitgesloten.size > 0 && (
                <span className="text-[11px] text-red-700 font-bold mr-2">
                  🚫 {uitgesloten.size} uitgesloten
                </span>
              )}
              <button onClick={onClose} className="btn btn-secondary">Annuleren</button>
              <button
                onClick={uitvoeren}
                disabled={busy || teImporteren === 0}
                className="btn btn-primary font-bold inline-flex items-center gap-2"
                data-testid="import-toepassen"
              >
                <CheckCircle size={14} weight="fill" /> {busy ? 'Bezig...' : `Toepassen (${teImporteren})`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Backup-historiek overlay */}
      {showBackups && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setShowBackups(false)} data-testid="import-backups-overlay">
          <div className="w-full max-w-3xl bg-white shadow-2xl flex flex-col" style={{ maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 bg-[#002FA7] text-white flex items-center justify-between">
              <h3 className="font-bold text-base inline-flex items-center gap-2">
                <ClockCounterClockwise size={18} weight="fill" /> Import-backups (laatste 20)
              </h3>
              <button onClick={() => setShowBackups(false)} className="p-1.5 hover:bg-white/10"><X size={18} weight="bold" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {backups.length === 0 ? (
                <div className="text-center text-gray-400 italic py-8">Nog geen import-backups beschikbaar.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr className="text-[10px] uppercase tracking-wider text-gray-600 font-bold">
                      <th className="text-left px-2 py-1.5">Datum</th>
                      <th className="text-left px-2 py-1.5">Label</th>
                      <th className="text-right px-2 py-1.5">Gewijzigd</th>
                      <th className="text-right px-2 py-1.5">Nieuw</th>
                      <th className="text-center px-2 py-1.5">Status</th>
                      <th className="text-center px-2 py-1.5">Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backups.map(b => {
                      const hersteld = b.status === 'hersteld';
                      const dt = b.timestamp ? new Date(b.timestamp).toLocaleString('nl-BE') : '';
                      return (
                        <tr key={b.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-2 py-2 font-mono text-[11px]">{dt}</td>
                          <td className="px-2 py-2">{b.label || '—'}</td>
                          <td className="px-2 py-2 text-right font-mono">{b.aantal_gewijzigd}</td>
                          <td className="px-2 py-2 text-right font-mono">{b.aantal_nieuw || 0}</td>
                          <td className="px-2 py-2 text-center">
                            {hersteld ? (
                              <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-500">Hersteld</span>
                            ) : (
                              <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-emerald-50 text-emerald-700">Actief</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center">
                            <button
                              onClick={() => herstelBackup(b.id)}
                              disabled={hersteld || busy}
                              className="btn btn-secondary btn-sm text-[10px]"
                              data-testid={`import-backup-herstel-${b.id}`}
                              title={hersteld ? 'Deze backup is al hersteld' : 'Rol alle wijzigingen terug'}
                            >
                              ↺ Herstel
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-200 text-[10px] text-gray-500">
              💡 Backups worden 20 stuks bewaard (oudere worden automatisch verwijderd). Herstellen zet ALLE artikels in deze backup terug naar de snapshot-versie.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, color, onClick, active }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`border-l-4 p-2.5 text-left transition-all ${onClick ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'} ${active ? 'ring-2 ring-offset-1' : ''}`}
      style={{ borderColor: color, ringColor: color }}
      data-testid={`import-kpi-${label.toLowerCase()}`}
    >
      <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{label}</div>
      <div className="text-xl font-bold mt-0.5 font-mono" style={{ color }}>{value}</div>
    </button>
  );
}
